import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Bell,
  Building2,
  CalendarClock,
  CalendarDays,
  Check,
  FileText,
  FolderTree,
  Hash,
  IndianRupee,
  MapPin,
  Settings,
  Sparkles,
  TreePalm,
  Users,
  Wand2,
} from "lucide-react";
import API from "../../services/api";
import SalaryPayoutModeField, { normalizeSalaryPayoutMode } from "../Salary/SalaryPayoutModeField";
import OrganisationLogo from "../../components/common/OrganisationLogo";
import SearchableSelect from "../../components/common/SearchableSelect";
import { getNextEmployeeCode } from "../Employees/EmployeeApi";
import { notifyOrganisationBrandingUpdated } from "../../utils/orgBranding";
import { OnboardingContext } from "../Context/OnboardingContext";
import { useFoundationSetup } from "../Context/FoundationSetupContext";
import { useSetupProgress } from "../Context/SetupProgressContext";
import "../../styles/IndianCalendar.css";
import "./setup.css";

// ─── Constants ───────────────────────────────────────────────────────────────

const INDUSTRY_OPTIONS = [
  "IT Services", "Manufacturing", "Healthcare", "Retail", "Construction",
  "Education", "Finance", "Logistics", "Hospitality", "Other",
];

const COMPANY_SIZES = [
  "1-10", "11-50", "51-200", "201-500", "501-1000", "1000+",
];

const BUSINESS_CATEGORIES = [
  "Private Limited", "Public Limited", "LLP", "Partnership",
  "Proprietorship", "NGO / Trust", "Government", "Other",
];

const ATTENDANCE_METHODS = [
  { value: "default_present", label: "Default Present", sublabel: "All employees marked present unless overridden" },
  { value: "manual", label: "Manual Entry", sublabel: "HR enters attendance manually" },
  { value: "biometric", label: "Biometric Device", sublabel: "Fingerprint or face reader integration" },
  { value: "web", label: "Website Login Entry", sublabel: "Employees mark attendance from web portal" },
  { value: "excel_upload", label: "Excel Upload", sublabel: "Import attendance from spreadsheet" },
];

const ATTENDANCE_METHOD_HINTS = {
  default_present: "Employees are treated as present on working days. Use Daily Attendance only to mark exceptions (leave, absent).",
  manual: "HR or managers enter attendance in Daily Attendance. No device or mobile check-in is used.",
  biometric: "Connect fingerprint or face readers. Register devices after setup from Attendance → Devices.",
  web: "Employees log in to the web portal and mark their own attendance each day.",
  excel_upload: "Upload monthly attendance sheets from Attendance → Import. No live check-in methods are active.",
};

/** Derive enabled flags from the single selected primary method (mutually exclusive). */
function flagsFromAttendanceMethod(method) {
  return {
    gps_enabled: false,
    selfie_enabled: false,
    mobile_enabled: false,
    web_enabled: method === "web",
    biometric_enabled: method === "biometric",
    manual_enabled: method === "manual",
  };
}

const TIMEZONES = [
  "Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Asia/Tokyo",
  "Europe/London", "America/New_York", "America/Los_Angeles", "UTC",
];

const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED", "SGD"];

const FY_OPTIONS = [
  { value: "april", label: "April – March (India)" },
  { value: "january", label: "January – December" },
  { value: "july", label: "July – June" },
];

const WEEK_DAYS = [
  { value: "monday", label: "Monday" },
  { value: "sunday", label: "Sunday" },
  { value: "saturday", label: "Saturday" },
];

const LEAVE_SKIP_KEY = "su_leave_setup_skipped";

/** One section per step — draft stays in memory until the final page saves. */
const SETUP_STEPS = [
  { id: "org-profile", title: "Organisation Profile", short: "Profile" },
  { id: "registration", title: "Company Registration", short: "Registration" },
  { id: "departments", title: "Departments", short: "Departments" },
  { id: "designations", title: "Designations", short: "Designations" },
  { id: "locations", title: "Work Locations", short: "Locations" },
  { id: "org-settings", title: "Organisation Settings", short: "Settings" },
  { id: "attendance", title: "Attendance Defaults", short: "Attendance" },
  { id: "leave", title: "Leave & Holidays", short: "Leave" },
  { id: "payroll", title: "Payroll Settings", short: "Payroll" },
  { id: "numbering", title: "Employee Numbering", short: "Numbering" },
  { id: "notifications", title: "Notifications", short: "Alerts" },
];

function isValidEmpPrefix(prefix) {
  return /^[A-Z]{1,4}$/.test(prefix);
}

const DEPT_SUGGESTIONS  = ["HR", "Finance", "Engineering", "Operations", "Sales", "Marketing", "Legal", "Admin"];
const DESIG_SUGGESTIONS = ["CEO", "Manager", "Team Lead", "Software Engineer", "Accountant", "HR Executive", "Analyst"];
const LOC_SUGGESTIONS   = ["Hyderabad", "Bangalore", "Chennai", "Mumbai", "Pune", "Delhi", "Noida", "Remote"];

const STRUCTURE_ACRONYMS = new Set([
  "HR", "IT", "CEO", "CFO", "CTO", "COO", "VP", "GM", "MD", "AI", "QA", "UI", "UX",
]);

function formatStructureLabel(value, format = "title") {
  const cleaned = String(value ?? "").trim().replace(/\s+/g, " ");
  if (!cleaned) return cleaned;

  if (format === "upper") {
    return cleaned.toUpperCase();
  }

  return cleaned
    .split(" ")
    .map((word) => {
      const match = word.match(/^([^a-zA-Z]*)([a-zA-Z]+)([^a-zA-Z]*)$/);
      if (!match) return word;
      const [, pre, core, post] = match;
      const upperCore = core.toUpperCase();
      if (STRUCTURE_ACRONYMS.has(upperCore) || (core.length <= 3 && /^[a-zA-Z]+$/.test(core))) {
        return `${pre}${upperCore}${post}`;
      }
      return `${pre}${core.charAt(0).toUpperCase()}${core.slice(1).toLowerCase()}${post}`;
    })
    .join(" ");
}

function formatStructureList(items, format = "title") {
  const seen = new Set();
  const out = [];
  for (const item of items || []) {
    const formatted = formatStructureLabel(item, format);
    if (!formatted) continue;
    const key = formatted.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(formatted);
  }
  return out;
}

// ─── Tiny helpers ─────────────────────────────────────────────────────────────

const Req = () => <span className="req">*</span>;

function Err({ msg }) {
  if (!msg) return null;
  return (
    <span className="su-err">
      <AlertTriangle size={12} strokeWidth={2} aria-hidden />
      {msg}
    </span>
  );
}

/** Visual order of Setup fields that can show validation errors. */
const SETUP_ERROR_FIELD_ORDER = [
  "orgName",
  "gstNumber",
  "panNumber",
  "departments",
  "designations",
  "locations",
  "attendanceMethod",
  "monthlySalaryDivisorDays",
  "empPrefix",
];

/** Scroll to the first field with a validation error and focus its control. */
function focusFirstSetupError(errors) {
  const keys = SETUP_ERROR_FIELD_ORDER.filter((k) => errors?.[k]);
  const fallback = Object.keys(errors || {}).filter((k) => errors[k]);
  const first = keys[0] || fallback[0];
  if (!first) return;

  const wrap = document.querySelector(`[data-field-key="${first}"]`);
  if (!wrap) return;

  wrap.scrollIntoView({ behavior: "smooth", block: "center" });
  const control = wrap.querySelector("input, select, textarea, button");
  setTimeout(() => {
    try {
      (control || wrap).focus?.({ preventScroll: true });
    } catch {
      /* ignore focus failures */
    }
  }, 280);
}

function SectionCard({ id, icon: Icon, iconColor = "navy", title, desc, children }) {
  return (
    <div className="su-section" id={id}>
      <div className="su-section-hdr">
        <div className={`su-section-icon ${iconColor}`}>
          {Icon ? <Icon size={18} strokeWidth={1.75} /> : null}
        </div>
        <div className="su-section-label">
          <h3>{title}</h3>
          {desc && <p>{desc}</p>}
        </div>
      </div>
      <div className="su-section-body">{children}</div>
    </div>
  );
}

function Toggle({ checked, onChange, id, disabled = false }) {
  return (
    <label className={`su-toggle${disabled ? " su-toggle--disabled" : ""}`} htmlFor={id}>
      <input type="checkbox" id={id} checked={checked} disabled={disabled} onChange={e => onChange(e.target.checked)} />
      <span className="su-toggle-track" />
    </label>
  );
}

function ToggleRow({ label, sub, checked, onChange, id, disabled = false }) {
  return (
    <div className={`su-toggle-row${disabled ? " su-toggle-row--disabled" : ""}`}>
      <div>
        <p className="su-toggle-label">{label}</p>
        {sub && <p className="su-toggle-sub">{sub}</p>}
      </div>
      <Toggle checked={checked} onChange={onChange} id={id} disabled={disabled} />
    </div>
  );
}

// ─── Tag Input ────────────────────────────────────────────────────────────────

function TagInput({ tags, onChange, suggestions = [], placeholder, hasError, formatLabel }) {
  const [draft, setDraft] = useState("");
  const [showSug, setShowSug] = useState(false);
  const inputRef = useRef(null);

  const filteredSugs = draft.length >= 1
    ? suggestions.filter(s =>
        s.toLowerCase().includes(draft.toLowerCase()) &&
        !tags.some(t => t.toLowerCase() === s.toLowerCase())
      )
    : [];

  const addTag = useCallback((val) => {
    const raw = val.trim();
    if (!raw) return;
    const v = formatLabel ? formatLabel(raw) : raw;
    if (!v) return;
    if (tags.some(t => t.toLowerCase() === v.toLowerCase())) return;
    onChange([...tags, v]);
    setDraft("");
    setShowSug(false);
  }, [tags, onChange, formatLabel]);

  const removeTag = (i) => onChange(tags.filter((_, idx) => idx !== i));

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(draft);
    } else if (e.key === "Backspace" && !draft && tags.length) {
      onChange(tags.slice(0, -1));
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <div
        className={`su-tags-box${hasError ? " err" : ""}`}
        onClick={() => inputRef.current?.focus()}
      >
        {tags.map((t, i) => (
          <span key={i} className="su-tag">
            {t}
            <button type="button" onClick={() => removeTag(i)}>×</button>
          </span>
        ))}
        <input
          ref={inputRef}
          className="su-tag-input"
          placeholder={tags.length === 0 ? placeholder : "Add more…"}
          value={draft}
          onChange={e => { setDraft(e.target.value); setShowSug(true); }}
          onKeyDown={onKey}
          onBlur={() => setTimeout(() => setShowSug(false), 150)}
          onFocus={() => draft && setShowSug(true)}
        />
      </div>
      {showSug && filteredSugs.length > 0 && (
        <ul className="su-tag-suggestion">
          {filteredSugs.map(s => (
            <li key={s} onMouseDown={() => addTag(s)}>{s}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Leave setup redirect ─────────────────────────────────────────────────────

function LeaveSetupCard({ navigate, skipped, onSkip, leaveConfigured }) {
  if (leaveConfigured) {
    return (
      <div className="su-leave-hub su-leave-hub--done">
        <div className="su-leave-hub-glow" aria-hidden />
        <div className="su-leave-hub-icon done">
          <Check size={22} strokeWidth={2.5} />
        </div>
        <div className="su-leave-hub-body">
          <h4>Leave module configured</h4>
          <p>Leave types are set up. You can manage policies, holidays, and balances anytime.</p>
        </div>
        <button
          type="button"
          className="su-leave-btn su-leave-btn--secondary"
          onClick={() => navigate("/settings/leave-setup")}
        >
          Open Leave Module <ArrowRight size={15} />
        </button>
      </div>
    );
  }

  return (
    <div className={`su-leave-hub${skipped ? " su-leave-hub--skipped" : ""}`}>
      <div className="su-leave-hub-glow" aria-hidden />
      <div className="su-leave-hub-icon">
        <TreePalm size={22} strokeWidth={1.75} />
      </div>
      <div className="su-leave-hub-body">
        <h4>Configure Leave &amp; Holidays</h4>
        <p>
          Set up leave types, holiday calendar, and policies in the dedicated Leave module.
          This keeps setup focused and gives you full control in one place.
        </p>
      </div>

      {skipped && (
        <div className="su-leave-warn" role="alert">
          <AlertTriangle size={16} strokeWidth={2} aria-hidden />
          <div>
            <strong>Leave setup skipped.</strong> You must configure leave types and holidays
            from the Leave module before employees can apply for leave.
          </div>
        </div>
      )}

      <div className="su-leave-hub-actions">
        <button
          type="button"
          className="su-leave-btn su-leave-btn--primary"
          onClick={() => navigate("/settings/leave-setup")}
        >
          <CalendarDays size={17} strokeWidth={2} />
          Go to Leave Module
          <ArrowRight size={16} className="su-leave-btn-arrow" />
        </button>
        {!skipped ? (
          <button type="button" className="su-leave-btn su-leave-btn--ghost" onClick={onSkip}>
            Skip for now
          </button>
        ) : null}
      </div>
    </div>
  );
}

// ─── Logo Upload ──────────────────────────────────────────────────────────────

function LogoUpload({ logoUrl, logoVersion, onLogoChange, onLogoRemove }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const fileRef = useRef(null);

  const doUpload = async (file) => {
    if (!file) return;
    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml", "image/webp"];
    if (!allowed.includes(file.type)) {
      setUploadErr("Only PNG, JPG, SVG, WEBP files are allowed.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadErr("File must be under 5 MB.");
      return;
    }
    setUploadErr("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await API.post("/setup/upload-logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onLogoChange(res.data.logo_url);
      notifyOrganisationBrandingUpdated({ logo_url: res.data.logo_url });
    } catch (e) {
      setUploadErr(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const onInputChange = (e) => doUpload(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    doUpload(e.dataTransfer.files?.[0]);
  };

  const handleRemove = async () => {
    try {
      await API.delete("/setup/logo");
    } catch {}
    onLogoRemove();
    notifyOrganisationBrandingUpdated({ logo_url: null });
  };

  return (
    <div>
      {logoUrl ? (
        <div>
          <div className="su-logo-preview">
            <OrganisationLogo
              logoUrl={logoUrl}
              cacheBust={logoVersion}
              className="su-logo-preview-img"
            />
          </div>
          <div className="su-logo-actions">
            <button type="button" className="su-logo-btn" onClick={() => fileRef.current.click()}>
              Replace
            </button>
            <button type="button" className="su-logo-btn danger" onClick={handleRemove}>
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div
          className={`su-logo-zone${dragOver ? " drag-over" : ""}`}
          onClick={() => fileRef.current.click()}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          <span className="su-logo-icon" aria-hidden>
            <Building2 size={28} strokeWidth={1.5} />
          </span>
          <p><strong>Click to upload</strong> or drag and drop</p>
          <span>PNG, JPG, SVG, WEBP — max 5 MB</span>
          {uploading && (
            <div className="su-upload-progress">
              <div className="su-upload-progress-bar" style={{ width: "60%" }} />
            </div>
          )}
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp" onChange={onInputChange} style={{ display: "none" }} />
      {uploadErr && <Err msg={uploadErr} />}
    </div>
  );
}

// ─── Summary Panel ────────────────────────────────────────────────────────────

function SummaryPanel({ form, departments, designations, locations, logoUrl }) {
  const rows = [
    { key: "Org Name",       val: form.orgName || "—" },
    { key: "Industry",       val: form.industryType || "—" },
    { key: "Departments",    val: String(departments.length) },
    { key: "Designations",   val: String(designations.length) },
    { key: "Locations",      val: String(locations.length) },
    { key: "Leave",          val: form.leaveStatus || "—" },
    { key: "Emp Prefix",     val: form.liveNextCode || form.empPrefix || "—" },
  ];

  const statusRow = (label, done) => (
    <div className="su-summary-row" key={label}>
      <span className="su-summary-key">{label}</span>
      <span className={`su-summary-badge ${done ? "ok" : "pend"}`}>
        {done ? (
          <>
            <Check size={11} strokeWidth={2.5} aria-hidden />
            Set
          </>
        ) : (
          "Pending"
        )}
      </span>
    </div>
  );

  const attReady = form.attendanceReady;
  const leaveReady = form.leaveReady;
  const payrollReady = form.payrollReady;

  return (
    <div className="su-summary-card">
      <div className="su-summary-hdr">
        <div className="su-summary-logo">
          <OrganisationLogo logoUrl={logoUrl} cacheBust={form.logoVersion} alt="logo" />
        </div>
        <h4>{form.orgName || "Your Organisation"}</h4>
        <p>{form.legalName || form.tradeName || "Complete setup below"}</p>
      </div>
      <div className="su-summary-body">
        {rows.map(r => (
          <div className="su-summary-row" key={r.key}>
            <span className="su-summary-key">{r.key}</span>
            <span className="su-summary-val">{r.val}</span>
          </div>
        ))}
        <div className="su-summary-divider" />
        {statusRow("Attendance", attReady)}
        {statusRow("Leave", leaveReady)}
        {statusRow("Payroll", payrollReady)}
      </div>
    </div>
  );
}

function SetupSkeleton() {
  return (
    <div className="su-skeleton-page">
      <div className="su-skeleton-bar" />
      <div className="su-body">
        <div className="su-main">
          {[1, 2, 3].map(i => (
            <div key={i} className="su-skeleton-card">
              <div className="su-skeleton-line w40" />
              <div className="su-skeleton-line w80" />
              <div className="su-skeleton-line w60" />
            </div>
          ))}
        </div>
        <div className="su-skeleton-panel" />
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Setup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const obCtx = useContext(OnboardingContext);
  const foundationCtx = useFoundationSetup();
  const setupProgressCtx = useSetupProgress();
  const [checkingSetup, setCheckingSetup] = useState(true);
  const [foundationComplete, setFoundationComplete] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [globalErr, setGlobalErr] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [errors, setErrors] = useState({});
  const [stepIndex, setStepIndex] = useState(0);

  // ── Org profile ───────────────────────────────────────────────────────────
  const [orgName,       setOrgName]       = useState("");
  const [legalName,     setLegalName]     = useState("");
  const [tradeName,     setTradeName]     = useState("");
  const [orgCode,       setOrgCode]       = useState("");
  const [industryType,  setIndustryType]  = useState("");
  const [businessCategory, setBusinessCategory] = useState("");
  const [companySize,   setCompanySize]   = useState("");
  const [logoUrl,       setLogoUrl]       = useState("");
  const [logoVersion,   setLogoVersion]   = useState(() => Date.now());

  // ── Statutory ─────────────────────────────────────────────────────────────
  const [gstNumber,     setGstNumber]     = useState("");
  const [panNumber,     setPanNumber]     = useState("");
  const [tanNumber,     setTanNumber]     = useState("");
  const [cinNumber,     setCinNumber]     = useState("");
  const [epfoCode,      setEpfoCode]      = useState("");
  const [esicCode,      setEsicCode]      = useState("");
  const [ptNumber,      setPtNumber]      = useState("");

  // ── Structure ─────────────────────────────────────────────────────────────
  const [departments,   setDepartments]   = useState([]);
  const [designations,  setDesignations]  = useState([]);
  const [locations,     setLocations]     = useState([]);
  const [structureLabelFormat] = useState("title");

  // ── Org settings ─────────────────────────────────────────────────────────
  const [financialYear, setFinancialYear] = useState("april");
  const [currency,      setCurrency]      = useState("INR");
  const [timezone,      setTimezone]      = useState("Asia/Kolkata");
  const [weekStart,     setWeekStart]     = useState("monday");

  // ── Attendance ────────────────────────────────────────────────────────────
  const [attendanceMethod, setAttendanceMethod] = useState("");
  const [, setGpsEnabled] = useState(false);
  const [, setSelfieEnabled] = useState(false);
  const [, setMobileEnabled] = useState(false);
  const [, setWebEnabled] = useState(false);
  const [, setBiometricEnabled] = useState(false);
  const [, setManualEnabled] = useState(false);
  const [shiftName,       setShiftName]       = useState("General Shift");
  const [shiftStart,      setShiftStart]      = useState("09:00");
  const [shiftEnd,        setShiftEnd]        = useState("18:00");
  const [graceMins,       setGraceMins]       = useState(15);
  const [breakDuration,   setBreakDuration]   = useState(60);
  const [workWeek,        setWorkWeek]        = useState("mon-fri");
  const [workingHours,    setWorkingHours]    = useState(9);

  // ── Leave setup status ────────────────────────────────────────────────────
  const [leaveConfigured, setLeaveConfigured] = useState(false);
  const [leaveSkipped, setLeaveSkipped] = useState(() => {
    try { return localStorage.getItem(LEAVE_SKIP_KEY) === "1"; } catch { return false; }
  });

  // ── Payroll settings ──────────────────────────────────────────────────────
  const [salaryCycle,          setSalaryCycle]          = useState("monthly");
  const [salaryPaymentDay,     setSalaryPaymentDay]     = useState(1);
  const [payrollProcessingDay, setPayrollProcessingDay] = useState(25);
  const [monthlySalaryDivisorDays, setMonthlySalaryDivisorDays] = useState(26);
  const [defaultSalaryStructure, setDefaultSalaryStructure] = useState(false);
  const [salaryPayoutMode, setSalaryPayoutMode] = useState("with_deductions");
  const [sampleAnnualCtc, setSampleAnnualCtc]         = useState("600000");

  // ── Admin employee ────────────────────────────────────────────────────────

  // ── Employee numbering ────────────────────────────────────────────────────
  const [empPrefix,        setEmpPrefix]        = useState("EMP");
  const [empStartNumber,   setEmpStartNumber]   = useState(1);
  const [empAutoIncrement, setEmpAutoIncrement] = useState(true);
  const [empUsePrefix,     setEmpUsePrefix]     = useState(true);
  const [liveNextCode,     setLiveNextCode]     = useState("EMP0001");
  const [liveCodeLoading,  setLiveCodeLoading]  = useState(false);
  const prefixDebounceRef = useRef(null);
  const successRedirectRef = useRef(null);
  const successBannerRef = useRef(null);

  // ── Notifications ─────────────────────────────────────────────────────────
  const [emailNotif,      setEmailNotif]      = useState(true);
  const [attendanceAlert, setAttendanceAlert] = useState(true);
  const [leaveAlert,      setLeaveAlert]      = useState(true);
  const [payrollAlert,    setPayrollAlert]    = useState(true);
  const [approvalNotif,   setApprovalNotif]   = useState(true);

  useEffect(() => {
    const focus = searchParams.get("focus");
    if (!focus) return;
    const idx = SETUP_STEPS.findIndex((s) => s.id === focus);
    if (idx >= 0) setStepIndex(idx);
  }, [searchParams]);

  // After a successful save: scroll success banner into view, then go to Dashboard.
  useEffect(() => {
    if (!successMsg) return undefined;

    const banner = successBannerRef.current || document.getElementById("setup-success-banner");
    if (banner) {
      requestAnimationFrame(() => {
        banner.scrollIntoView({ behavior: "smooth", block: "center" });
        if (!banner.hasAttribute("tabindex")) banner.tabIndex = -1;
        try { banner.focus({ preventScroll: true }); } catch {}
      });
    }

    if (successRedirectRef.current) clearTimeout(successRedirectRef.current);
    successRedirectRef.current = setTimeout(() => {
      navigate("/dashboard", { replace: true });
    }, 1800);

    return () => {
      if (successRedirectRef.current) clearTimeout(successRedirectRef.current);
    };
  }, [successMsg, navigate]);

  // ── Check if already setup, pre-fill existing data ───────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await API.get("/setup/status");
        if (cancelled) return;

        const isComplete = !!res?.data?.foundation_complete;
        setFoundationComplete(isComplete);

        const structureFormat = "title";
        const hasStructureData =
          (res?.data?.departments_count ?? 0) > 0
          || (res?.data?.designations_count ?? 0) > 0
          || (res?.data?.locations_count ?? 0) > 0
          || isComplete;

        if (hasStructureData) {
          const [deptRes, desRes, locRes] = await Promise.all([
            API.get("/setup/departments"),
            API.get("/setup/designations"),
            API.get("/setup/locations"),
          ]);
          if (!cancelled) {
            setDepartments(formatStructureList((deptRes.data || []).map(d => d.name), structureFormat));
            setDesignations(formatStructureList((desRes.data || []).map(d => d.name), structureFormat));
            setLocations(formatStructureList((locRes.data || []).map(l => l.name), structureFormat));
          }
        }

        // Pre-fill from existing org data
        const o = res?.data?.org;
        if (o) {
          if (o.name)                setOrgName(o.name);
          if (o.legal_name)          setLegalName(o.legal_name);
          if (o.trade_name)          setTradeName(o.trade_name);
          if (o.registration_no)     setOrgCode(o.registration_no);
          if (o.industry_type)       setIndustryType(o.industry_type);
          if (o.business_category)   setBusinessCategory(o.business_category);
          if (o.company_size)        setCompanySize(o.company_size);
          if (o.default_salary_structure != null) setDefaultSalaryStructure(!!o.default_salary_structure);
          if (o.salary_payout_mode) setSalaryPayoutMode(normalizeSalaryPayoutMode(o.salary_payout_mode));
          if (o.logo_url) {
            setLogoUrl(o.logo_url);
            setLogoVersion(Date.now());
          }
          if (o.timezone)            setTimezone(o.timezone);
          if (o.pan)                 setPanNumber(o.pan);
          if (o.gst_number)          setGstNumber(o.gst_number);
          if (o.tan_number)          setTanNumber(o.tan_number);
          if (o.cin_number)          setCinNumber(o.cin_number);
          if (o.epfo_code)           setEpfoCode(o.epfo_code);
          if (o.esic_code)           setEsicCode(o.esic_code);
          if (o.professional_tax_number) setPtNumber(o.professional_tax_number);
          if (o.financial_year_start) setFinancialYear(o.financial_year_start);
          if (o.currency)            setCurrency(o.currency);
          if (o.week_start_day)      setWeekStart(o.week_start_day);
          if (o.salary_cycle)        setSalaryCycle(o.salary_cycle);
          if (o.salary_payment_day)  setSalaryPaymentDay(o.salary_payment_day);
          if (o.payroll_processing_day) setPayrollProcessingDay(o.payroll_processing_day);
          if (o.monthly_salary_divisor_days != null) {
            setMonthlySalaryDivisorDays(o.monthly_salary_divisor_days);
          }
          if (o.employee_prefix)     setEmpPrefix(o.employee_prefix);
          if (o.employee_number_start) setEmpStartNumber(o.employee_number_start);
          if (o.employee_auto_increment != null) setEmpAutoIncrement(!!o.employee_auto_increment);
          if (o.employee_use_prefix != null) setEmpUsePrefix(!!o.employee_use_prefix);
          else if (o.employee_numbering?.use_prefix != null) setEmpUsePrefix(!!o.employee_numbering.use_prefix);
          const ad = o.attendance_defaults || {};
          if (ad.attendance_method) {
            const m = ad.attendance_method;
            const valid = ATTENDANCE_METHODS.some((o) => o.value === m);
            if (valid) {
              setAttendanceMethod(m);
              const flags = flagsFromAttendanceMethod(m);
              setGpsEnabled(flags.gps_enabled);
              setSelfieEnabled(flags.selfie_enabled);
              setMobileEnabled(flags.mobile_enabled);
              setWebEnabled(flags.web_enabled);
              setBiometricEnabled(flags.biometric_enabled);
              setManualEnabled(flags.manual_enabled);
            }
          } else {
            if (ad.gps_enabled != null) setGpsEnabled(ad.gps_enabled);
            if (ad.selfie_enabled != null) setSelfieEnabled(ad.selfie_enabled);
            if (ad.mobile_enabled != null) setMobileEnabled(ad.mobile_enabled);
            if (ad.web_enabled != null) setWebEnabled(ad.web_enabled);
            if (ad.biometric_enabled != null) setBiometricEnabled(ad.biometric_enabled);
            if (ad.manual_enabled != null) setManualEnabled(ad.manual_enabled);
          }
          if (ad.shift_name) setShiftName(ad.shift_name);
          if (ad.shift_start) setShiftStart(ad.shift_start);
          if (ad.shift_end) setShiftEnd(ad.shift_end);
          if (ad.grace_minutes != null) setGraceMins(ad.grace_minutes);
          if (ad.break_duration != null) setBreakDuration(ad.break_duration);
          if (ad.work_week) setWorkWeek(ad.work_week);
          if (ad.working_hours != null) setWorkingHours(ad.working_hours);
          const notif = o.notifications || {};
          if (notif.email_notifications != null) setEmailNotif(notif.email_notifications);
          if (notif.attendance_alerts != null) setAttendanceAlert(notif.attendance_alerts);
          if (notif.leave_alerts != null) setLeaveAlert(notif.leave_alerts);
          if (notif.payroll_alerts != null) setPayrollAlert(notif.payroll_alerts);
          if (notif.approval_notifications != null) setApprovalNotif(notif.approval_notifications);
        }
      } catch {}
      finally {
        if (!cancelled) setCheckingSetup(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get("/leave/types");
        if (!cancelled) setLeaveConfigured(Array.isArray(data) && data.length > 0);
      } catch {
        if (!cancelled) setLeaveConfigured(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleEmpPrefixChange = (raw) => {
    const cleaned = String(raw ?? "").replace(/[^a-zA-Z]/g, "").toUpperCase().slice(0, 4);
    setEmpPrefix(cleaned);
  };

  const resolvedPrefix = isValidEmpPrefix(empPrefix) ? empPrefix : null;

  const salaryPreview = useMemo(() => {
    if (!defaultSalaryStructure) return null;
    const ctc = Number(sampleAnnualCtc);
    const days = Number(monthlySalaryDivisorDays);
    if (!Number.isFinite(ctc) || ctc <= 0 || !Number.isFinite(days) || days < 1 || days > 31) {
      return null;
    }
    const monthly = ctc / 12;
    const daily = monthly / days;
    return {
      ctc,
      monthly,
      daily,
      days,
      monthlyFmt: monthly.toLocaleString("en-IN", { maximumFractionDigits: 0 }),
      dailyFmt: daily.toLocaleString("en-IN", { maximumFractionDigits: 0 }),
      ctcFmt: ctc.toLocaleString("en-IN"),
    };
  }, [defaultSalaryStructure, sampleAnnualCtc, monthlySalaryDivisorDays]);

  useEffect(() => {
    const num = Number(empStartNumber) || 1;
    const padded = String(num).padStart(Math.max(4, String(num).length), "0");
    if (!empUsePrefix) {
      setLiveNextCode(padded);
      return undefined;
    }
    if (!empAutoIncrement) {
      const p = resolvedPrefix || empPrefix || "EMP";
      setLiveNextCode(`${p}${padded}`);
      return undefined;
    }
    if (!resolvedPrefix) {
      setLiveNextCode(empPrefix.length === 0 ? "—" : "…");
      return undefined;
    }
    clearTimeout(prefixDebounceRef.current);
    prefixDebounceRef.current = setTimeout(async () => {
      setLiveCodeLoading(true);
      try {
        const data = await getNextEmployeeCode(resolvedPrefix, { use_prefix: true });
        setLiveNextCode(data?.employee_code || `${resolvedPrefix}${padded}`);
      } catch {
        setLiveNextCode(`${resolvedPrefix}${padded}`);
      } finally {
        setLiveCodeLoading(false);
      }
    }, 280);
    return () => clearTimeout(prefixDebounceRef.current);
  }, [empPrefix, resolvedPrefix, empStartNumber, empAutoIncrement, empUsePrefix]);

  const handleLeaveSkip = () => {
    setLeaveSkipped(true);
    try { localStorage.setItem(LEAVE_SKIP_KEY, "1"); } catch {}
  };

  useEffect(() => {
    if (leaveConfigured) {
      setLeaveSkipped(false);
      try { localStorage.removeItem(LEAVE_SKIP_KEY); } catch {}
    }
  }, [leaveConfigured]);

  const formatStructureTag = useCallback(
    (value) => formatStructureLabel(value, structureLabelFormat),
    [structureLabelFormat],
  );

  const handleAttendanceMethodChange = (method) => {
    setAttendanceMethod(method);
    const flags = flagsFromAttendanceMethod(method);
    setGpsEnabled(flags.gps_enabled);
    setSelfieEnabled(flags.selfie_enabled);
    setMobileEnabled(flags.mobile_enabled);
    setWebEnabled(flags.web_enabled);
    setBiometricEnabled(flags.biometric_enabled);
    setManualEnabled(flags.manual_enabled);
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = (scope = "all") => {
    const e = {};
    const check = (id) => scope === "all" || scope === id;

    if (check("org-profile") && !orgName.trim()) e.orgName = "Organisation name is required.";

    if (check("registration")) {
      if (gstNumber && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(gstNumber.trim().toUpperCase()))
        e.gstNumber = "Invalid GST format (e.g. 29ABCDE1234F1Z5).";
      if (panNumber && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(panNumber.trim().toUpperCase()))
        e.panNumber = "Invalid PAN format (e.g. ABCDE1234F).";
    }

    if (check("departments") && departments.length === 0) e.departments = "Add at least one department.";
    if (check("designations") && designations.length === 0) e.designations = "Add at least one designation.";
    if (check("locations") && locations.length === 0) e.locations = "Add at least one location.";
    if (check("attendance") && !attendanceMethod) e.attendanceMethod = "Select a primary attendance method.";

    if (check("payroll")) {
      const divisorDays = Number(monthlySalaryDivisorDays);
      if (!Number.isFinite(divisorDays) || divisorDays < 1 || divisorDays > 31) {
        e.monthlySalaryDivisorDays = "Enter working days between 1 and 31.";
      }
    }

    if (check("numbering") && empUsePrefix && empAutoIncrement && !resolvedPrefix) {
      e.empPrefix = empPrefix.length === 0
        ? "Employee prefix is required (1–4 letters, e.g. EMP), or turn prefix off for numbers only."
        : "Prefix must be 1–4 letters (e.g. EMP, HR, AAA).";
    }

    setErrors(e);
    return e;
  };

  const goToStep = (idx) => {
    setStepIndex(Math.max(0, Math.min(SETUP_STEPS.length - 1, idx)));
    setGlobalErr("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNext = () => {
    const stepId = SETUP_STEPS[stepIndex]?.id;
    const fieldErrors = validate(stepId);
    if (Object.keys(fieldErrors).length > 0) {
      setGlobalErr("Please fix the highlighted fields before continuing.");
      requestAnimationFrame(() => {
        setTimeout(() => focusFirstSetupError(fieldErrors), 80);
      });
      return;
    }
    goToStep(stepIndex + 1);
  };

  const handleBack = () => {
    setErrors({});
    goToStep(stepIndex - 1);
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setGlobalErr("");
    setSuccessMsg("");
    const fieldErrors = validate();
    if (Object.keys(fieldErrors).length > 0) {
      const firstErrorKey = SETUP_ERROR_FIELD_ORDER.find((k) => fieldErrors[k]) || Object.keys(fieldErrors)[0];
      const stepForError = {
        orgName: "org-profile",
        gstNumber: "registration",
        panNumber: "registration",
        departments: "departments",
        designations: "designations",
        locations: "locations",
        attendanceMethod: "attendance",
        monthlySalaryDivisorDays: "payroll",
        empPrefix: "numbering",
      }[firstErrorKey];
      if (stepForError) {
        const idx = SETUP_STEPS.findIndex((s) => s.id === stepForError);
        if (idx >= 0) setStepIndex(idx);
      }
      setGlobalErr("Please fix the highlighted fields below before saving.");
      requestAnimationFrame(() => {
        setTimeout(() => focusFirstSetupError(fieldErrors), 120);
      });
      return;
    }

    const payload = {
      // Required (existing)
      departments,
      designations,
      locations,
      manager: {
        name:        "",
        email:       "",
      },

      // Org profile
      name:           orgName.trim(),
      legal_name:     legalName.trim() || undefined,
      logo_url:       logoUrl || undefined,
      trade_name:     tradeName.trim() || undefined,
      registration_no: orgCode.trim() || undefined,
      industry_type:  industryType || undefined,
      business_category: businessCategory || undefined,
      company_size:   companySize || undefined,
      pan:            panNumber.trim().toUpperCase() || undefined,

      // Statutory
      gst_number:            gstNumber.trim().toUpperCase() || undefined,
      tan_number:            tanNumber.trim().toUpperCase() || undefined,
      cin_number:            cinNumber.trim().toUpperCase() || undefined,
      epfo_code:             epfoCode.trim() || undefined,
      esic_code:             esicCode.trim() || undefined,
      professional_tax_number: ptNumber.trim() || undefined,

      // Org settings
      financial_year_start: financialYear,
      currency,
      timezone,
      week_start_day: weekStart,

      // Attendance defaults
      attendance_defaults: {
        attendance_method: attendanceMethod,
        ...flagsFromAttendanceMethod(attendanceMethod),
        shift_name:       shiftName,
        shift_start:      shiftStart,
        shift_end:        shiftEnd,
        grace_minutes:    Number(graceMins),
        break_duration:   Number(breakDuration),
        work_week:        workWeek,
        working_hours:    Number(workingHours),
      },

      // Leave types configured separately in Leave module
      leave_types: [],

      // Payroll
      payroll_defaults: {
        salary_cycle:           salaryCycle,
        salary_payment_day:     Number(salaryPaymentDay),
        payroll_processing_day: Number(payrollProcessingDay),
        monthly_salary_divisor_days: Number(monthlySalaryDivisorDays),
        default_salary_structure: defaultSalaryStructure,
        salary_payout_mode: salaryPayoutMode,
      },

      // Numbering
      employee_numbering: {
        prefix:         empUsePrefix ? (resolvedPrefix || "EMP") : "",
        start_number:   Number(empStartNumber) || 1,
        auto_increment: empAutoIncrement,
        use_prefix:     empUsePrefix,
      },

      // Notifications
      notifications: {
        email_notifications:   emailNotif,
        attendance_alerts:     attendanceAlert,
        leave_alerts:          leaveAlert,
        payroll_alerts:        payrollAlert,
        approval_notifications: approvalNotif,
      },
    };

    setSubmitting(true);
    setGlobalErr("");
    try {
      if (foundationComplete) {
        await API.patch("/setup/profile", payload);
        setSuccessMsg("All changes saved successfully. Taking you to the Dashboard…");
        setLogoVersion(Date.now());
        notifyOrganisationBrandingUpdated({ logo_url: logoUrl });
      } else {
        await API.post("/setup/extended", payload);
        setSuccessMsg("Organisation setup completed successfully. Taking you to the Dashboard…");
      }

      const statusRes = await API.get("/setup/status");
      const nowComplete = !!statusRes?.data?.foundation_complete;
      setFoundationComplete(nowComplete);
      if (foundationCtx?.refetch) await foundationCtx.refetch();
      if (setupProgressCtx?.refetch) await setupProgressCtx.refetch();
      if (obCtx?.refetch) await obCtx.refetch();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "Already setup completed") {
        setSuccessMsg("Organisation setup is already complete. Taking you to the Dashboard…");
        setFoundationComplete(true);
        return;
      }
      setGlobalErr(detail || "Setup failed. Please try again.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSubmitting(false);
    }
  };

  if (checkingSetup) {
    return <SetupSkeleton />;
  }

  const attendanceReady = Boolean(attendanceMethod);
  const leaveReady = leaveConfigured;
  const payrollReady = foundationComplete && Boolean(salaryCycle);
  const leaveStatusLabel = leaveConfigured ? "Configured" : leaveSkipped ? "Skipped" : "Pending";
  const currentStep = SETUP_STEPS[stepIndex] || SETUP_STEPS[0];
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === SETUP_STEPS.length - 1;
  const stepId = currentStep.id;

  const empPreview = liveNextCode && liveNextCode !== "—" && liveNextCode !== "…"
    ? liveNextCode
    : !empUsePrefix
      ? String(Number(empStartNumber) || 1).padStart(4, "0")
      : resolvedPrefix
        ? `${resolvedPrefix}${String(Number(empStartNumber) || 1).padStart(4, "0")}`
        : "—";

  return (
    <div className="su-root">
      <header className="su-hero">
        <div className="su-hero-inner">
          <div className="su-hero-icon" aria-hidden>
            <Wand2 size={22} strokeWidth={1.75} />
          </div>
          <div className="su-hero-copy">
            <div className="su-hero-badge">
              <Sparkles size={10} />
              Setup Wizard
            </div>
            <h1 className="su-hero-title">Organisation Setup</h1>
            <p className="su-hero-sub">
              Step {stepIndex + 1} of {SETUP_STEPS.length}: {currentStep.title}. Draft is kept in this session — nothing is saved until the last step.
            </p>
            <div className="su-stepper" role="navigation" aria-label="Setup steps">
              {SETUP_STEPS.map((step, idx) => (
                <button
                  key={step.id}
                  type="button"
                  className={`su-stepper-dot${idx === stepIndex ? " is-active" : ""}${idx < stepIndex ? " is-done" : ""}`}
                  title={step.title}
                  aria-current={idx === stepIndex ? "step" : undefined}
                  onClick={() => {
                    if (idx < stepIndex) goToStep(idx);
                  }}
                  disabled={idx > stepIndex}
                >
                  <span className="su-stepper-num">{idx < stepIndex ? <Check size={12} strokeWidth={3} /> : idx + 1}</span>
                  <span className="su-stepper-label">{step.short}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* Global error */}
      {successMsg && (
        <div style={{ padding: "12px 32px 0" }}>
          <div
            id="setup-success-banner"
            ref={successBannerRef}
            className="su-banner ok"
            role="alert"
            data-auto-scroll-alert="true"
          >
            <Check size={14} strokeWidth={2.5} aria-hidden />
            {successMsg}
          </div>
        </div>
      )}

      {foundationComplete && !successMsg && (
        <div style={{ padding: "12px 32px 0" }}>
          <div className="su-banner ok">
            <Check size={14} strokeWidth={2.5} aria-hidden />
            Foundation setup is complete. Review any step below, then click <strong>Save Changes</strong> on the last page.
            Logo uploads save immediately; other fields save only on the final step.
          </div>
        </div>
      )}

      {globalErr && (
        <div style={{ padding: "12px 32px 0" }}>
          <div className="su-banner err">
            <AlertTriangle size={14} strokeWidth={2} aria-hidden />
            {globalErr}
          </div>
        </div>
      )}

      {leaveSkipped && !leaveConfigured && (
        <div style={{ padding: "12px 32px 0" }}>
          <div className="su-banner warn">
            <AlertTriangle size={14} strokeWidth={2} aria-hidden />
            <span>
              <strong>Action required:</strong> Leave types and holidays are not configured yet.
              Open the <button type="button" className="su-inline-link" onClick={() => navigate("/settings/leave-setup")}>Leave module</button> to complete this before employees apply for leave.
            </span>
          </div>
        </div>
      )}

      <div className="su-body">
        {/* ── Left: current step only ───────────────────────────── */}
        <div className="su-main">

          {/* 1. ORGANIZATION PROFILE */}
          {stepId === "org-profile" && (
          <SectionCard icon={Building2} iconColor="blue" title="Organisation Profile" desc="Basic details visible across all modules">
            <div className="su-grid-2">
              <div className="su-field su-span-2">
                <label className="su-label">Company Logo</label>
                <LogoUpload
                  logoUrl={logoUrl}
                  logoVersion={logoVersion}
                  onLogoChange={(url) => {
                    setLogoUrl(url);
                    setLogoVersion(Date.now());
                  }}
                  onLogoRemove={() => {
                    setLogoUrl("");
                    setLogoVersion(Date.now());
                  }}
                />
                <span className="su-hint">Logo upload saves instantly. All other fields save only when you finish the last step.</span>
              </div>

              <div className="su-field su-span-2" data-field-key="orgName">
                <label className="su-label">Organisation Name <Req /></label>
                <input
                  className={`su-input${errors.orgName ? " err" : ""}`}
                  value={orgName}
                  onChange={e => setOrgName(e.target.value)}
                  placeholder="e.g. Acme Technologies Pvt. Ltd."
                />
                <Err msg={errors.orgName} />
              </div>

              <div className="su-field">
                <label className="su-label">Legal Company Name</label>
                <input className="su-input" value={legalName} onChange={e => setLegalName(e.target.value)} placeholder="Registered legal name" />
              </div>

              <div className="su-field">
                <label className="su-label">Trade Name / Brand Name</label>
                <input className="su-input" value={tradeName} onChange={e => setTradeName(e.target.value)} placeholder="Brand or DBA name" />
              </div>

              <div className="su-field">
                <label className="su-label">Organization Code</label>
                <input
                  className="su-input"
                  value={orgCode}
                  onChange={e => setOrgCode(e.target.value.toUpperCase())}
                  placeholder="e.g. ACME-HQ"
                  style={{ fontFamily: "monospace", letterSpacing: "0.04em" }}
                />
                <span className="su-hint">Unique internal code for reports and exports</span>
              </div>

              <div className="su-field">
                <SearchableSelect
                  label="Industry Type"
                  value={industryType}
                  options={INDUSTRY_OPTIONS.map((o) => ({ value: o, label: o }))}
                  allowEmpty
                  emptyLabel="Select industry"
                  searchPlaceholder="Search industry…"
                  onChange={setIndustryType}
                />
              </div>

              <div className="su-field">
                <SearchableSelect
                  label="Business Category"
                  value={businessCategory}
                  options={BUSINESS_CATEGORIES.map((o) => ({ value: o, label: o }))}
                  allowEmpty
                  emptyLabel="Select category"
                  searchPlaceholder="Search category…"
                  onChange={setBusinessCategory}
                />
              </div>

              <div className="su-field">
                <SearchableSelect
                  label="Company Size"
                  value={companySize}
                  options={COMPANY_SIZES.map((o) => ({ value: o, label: o }))}
                  allowEmpty
                  emptyLabel="Select size"
                  searchPlaceholder="Search size range…"
                  onChange={setCompanySize}
                />
              </div>
            </div>
          </SectionCard>
          )}

          {/* 2. COMPANY REGISTRATION */}
          {stepId === "registration" && (
          <SectionCard icon={FileText} iconColor="purple" title="Company Registration" desc="Indian statutory registration numbers — PAN and TAN are optional">
            <div className="su-grid-2">
              {[
                { key: "gstNumber", label: "GST Number", val: gstNumber, set: setGstNumber, err: errors.gstNumber, placeholder: "29ABCDE1234F1Z5" },
                { key: "panNumber", label: "PAN Number (optional)", val: panNumber, set: setPanNumber, err: errors.panNumber, placeholder: "ABCDE1234F" },
                { key: "tanNumber", label: "TAN Number (optional)", val: tanNumber, set: setTanNumber, err: null, placeholder: "MUMA12345A" },
                { key: "cinNumber", label: "CIN Number", val: cinNumber, set: setCinNumber, err: null, placeholder: "U72200MH2020PTC123456" },
                { key: "epfoCode", label: "EPFO Registration Code", val: epfoCode, set: setEpfoCode, err: null, placeholder: "KA/1234/567" },
                { key: "esicCode", label: "ESIC Registration Number", val: esicCode, set: setEsicCode, err: null, placeholder: "53000012345" },
                { key: "ptNumber", label: "Professional Tax Number", val: ptNumber, set: setPtNumber, err: null, placeholder: "PT registration number", hint: "Organisation-level PT — applies to all employees (not entered per employee)." },
              ].map(({ key, label, val, set, err, placeholder, hint }) => (
                <div className="su-field" key={label} data-field-key={key}>
                  <label className="su-label">{label}</label>
                  <input
                    className={`su-input${err ? " err" : ""}`}
                    value={val}
                    onChange={e => set(e.target.value.toUpperCase())}
                    placeholder={placeholder}
                    style={{ textTransform: "uppercase", fontFamily: "monospace", letterSpacing: "0.04em" }}
                  />
                  <Err msg={err} />
                  {hint ? <span className="su-hint">{hint}</span> : null}
                </div>
              ))}
            </div>
          </SectionCard>
          )}

          {/* 3. DEPARTMENTS */}
          {stepId === "departments" && (
          <SectionCard id="setup-departments" icon={FolderTree} iconColor="green" title="Departments" desc="Add at least one department — continue to designations next">
            <div className="su-field" data-field-key="departments">
              <label className="su-label">Departments <Req /></label>
              <TagInput
                tags={departments}
                onChange={setDepartments}
                suggestions={DEPT_SUGGESTIONS}
                placeholder="Type and press Enter — e.g. engineering"
                hasError={!!errors.departments}
                formatLabel={formatStructureTag}
              />
              <Err msg={errors.departments} />
              <span className="su-hint">
                Add your department names (e.g. Sales, Engineering, HR). Press Enter or comma to add; click × to remove.
              </span>
            </div>
          </SectionCard>
          )}

          {/* 4. DESIGNATIONS */}
          {stepId === "designations" && (
          <SectionCard id="setup-designations" icon={Users} iconColor="green" title="Designations" desc="Add job titles used across payroll and people hub">
            <div className="su-field" data-field-key="designations">
              <label className="su-label">Designations <Req /></label>
              <TagInput
                tags={designations}
                onChange={setDesignations}
                suggestions={DESIG_SUGGESTIONS}
                placeholder="Type and press Enter — e.g. software engineer"
                hasError={!!errors.designations}
                formatLabel={formatStructureTag}
              />
              <Err msg={errors.designations} />
              <span className="su-hint">
                Add job titles used in your organisation (e.g. Manager, Team Lead, Developer). Press Enter or comma to add; click × to remove.
              </span>
            </div>
          </SectionCard>
          )}

          {/* 5. LOCATIONS */}
          {stepId === "locations" && (
          <SectionCard id="setup-locations" icon={MapPin} iconColor="green" title="Work Locations" desc="Add office locations / branches">
            <div className="su-field" data-field-key="locations">
              <label className="su-label">Work Locations <Req /></label>
              <TagInput
                tags={locations}
                onChange={setLocations}
                suggestions={LOC_SUGGESTIONS}
                placeholder="Type and press Enter — e.g. mumbai"
                hasError={!!errors.locations}
                formatLabel={formatStructureTag}
              />
              <Err msg={errors.locations} />
              <span className="su-hint">
                Add your office or branch locations (e.g. Mumbai, Bangalore, Hyderabad). Press Enter or comma to add; click × to remove.
              </span>
            </div>
          </SectionCard>
          )}

          {/* 6. ORGANIZATION SETTINGS */}
          {stepId === "org-settings" && (
          <SectionCard icon={Settings} iconColor="amber" title="Organisation Settings" desc="Financial year, currency, timezone">
            <div className="su-grid-2">
              <div className="su-field">
                <SearchableSelect
                  label="Financial Year"
                  value={financialYear}
                  options={FY_OPTIONS}
                  allowEmpty={false}
                  onChange={setFinancialYear}
                />
              </div>
              <div className="su-field">
                <SearchableSelect
                  label="Currency"
                  value={currency}
                  options={CURRENCIES.map((c) => ({ value: c, label: c }))}
                  allowEmpty={false}
                  onChange={setCurrency}
                />
              </div>
              <div className="su-field">
                <SearchableSelect
                  label="Timezone"
                  value={timezone}
                  options={TIMEZONES.map((t) => ({ value: t, label: t }))}
                  allowEmpty={false}
                  searchPlaceholder="Search timezone…"
                  onChange={setTimezone}
                />
              </div>
              <div className="su-field">
                <SearchableSelect
                  label="Week Start Day"
                  value={weekStart}
                  options={WEEK_DAYS}
                  allowEmpty={false}
                  onChange={setWeekStart}
                />
              </div>
            </div>
          </SectionCard>
          )}

          {/* 7. ATTENDANCE DEFAULTS */}
          {stepId === "attendance" && (
          <SectionCard icon={CalendarClock} iconColor="teal" title="Attendance Defaults" desc="Choose one method — shift and timing can be configured later in Attendance">
            <div className="su-field" style={{ marginBottom: 16 }} data-field-key="attendanceMethod">
              <SearchableSelect
                label="Primary Attendance Method"
                value={attendanceMethod}
                options={ATTENDANCE_METHODS}
                allowEmpty
                emptyLabel="Select attendance method…"
                onChange={handleAttendanceMethodChange}
                required
              />
              <Err msg={errors.attendanceMethod} />
            </div>

            {attendanceMethod && (
              <div className="su-method-hint">
                <p>{ATTENDANCE_METHOD_HINTS[attendanceMethod]}</p>
              </div>
            )}

            {attendanceMethod === "biometric" && (
              <div className="su-method-hint su-method-hint--info" style={{ marginTop: 16 }}>
                <p>After saving setup, go to <strong>Attendance → Devices</strong> to register your biometric devices.</p>
              </div>
            )}
          </SectionCard>
          )}

          {/* 8. LEAVE — redirect to Leave module */}
          {stepId === "leave" && (
          <SectionCard icon={TreePalm} iconColor="sky" title="Leave &amp; Holidays" desc="Configure leave types and holiday calendar in the Leave module">
            <LeaveSetupCard
              navigate={navigate}
              skipped={leaveSkipped}
              onSkip={handleLeaveSkip}
              leaveConfigured={leaveConfigured}
            />
          </SectionCard>
          )}

          {/* 9. PAYROLL SETTINGS */}
          {stepId === "payroll" && (
          <SectionCard icon={IndianRupee} iconColor="rose" title="Payroll Settings" desc="Salary cycle and processing schedule">
            <div className="su-grid-3">
              <div className="su-field">
                <SearchableSelect
                  label="Salary Cycle"
                  value={salaryCycle}
                  options={[
                    { value: "monthly", label: "Monthly" },
                    { value: "biweekly", label: "Bi-Weekly" },
                    { value: "weekly", label: "Weekly" },
                  ]}
                  allowEmpty={false}
                  onChange={setSalaryCycle}
                />
              </div>
              <div className="su-field">
                <label className="su-label">Salary Payment Day</label>
                <input
                  className="su-input" type="number" min={1} max={31}
                  value={salaryPaymentDay}
                  onChange={e => setSalaryPaymentDay(e.target.value)}
                  placeholder="1"
                />
                <span className="su-hint">Day of month salary is paid</span>
              </div>
              <div className="su-field">
                <label className="su-label">Payroll Processing Day</label>
                <input
                  className="su-input" type="number" min={1} max={31}
                  value={payrollProcessingDay}
                  onChange={e => setPayrollProcessingDay(e.target.value)}
                  placeholder="25"
                />
                <span className="su-hint">Day payroll is processed</span>
              </div>
              <div className="su-field" data-field-key="monthlySalaryDivisorDays">
                <label className="su-label">Working Days</label>
                <input
                  className={`su-input${errors.monthlySalaryDivisorDays ? " err" : ""}`}
                  type="number"
                  min={1}
                  max={31}
                  value={monthlySalaryDivisorDays}
                  onChange={e => setMonthlySalaryDivisorDays(e.target.value)}
                  placeholder="26"
                />
                <span className="su-hint">Fixed days to divide monthly gross salary (e.g. 21 or 26)</span>
                <Err msg={errors.monthlySalaryDivisorDays} />
              </div>
            </div>
            <div style={{ marginTop: 14 }}>
              <ToggleRow
                label="Enable Default Salary Structure"
                sub="Creates Standard Package template — monthly gross = Annual CTC ÷ 12, daily rate uses working days below"
                checked={defaultSalaryStructure}
                onChange={setDefaultSalaryStructure}
                id="payroll-default-structure"
              />
            </div>
            <SalaryPayoutModeField value={salaryPayoutMode} onChange={setSalaryPayoutMode} />

            {defaultSalaryStructure && (
              <div className="su-salary-preview">
                <div className="su-salary-preview-hd">
                  <IndianRupee size={18} strokeWidth={2} />
                  <span>Live salary calculation preview</span>
                </div>
                <div className="su-salary-preview-grid">
                  <div className="su-field">
                    <label className="su-label">Sample annual CTC (₹)</label>
                    <input
                      className="su-input"
                      type="number"
                      min={1}
                      step={1000}
                      value={sampleAnnualCtc}
                      onChange={(e) => setSampleAnnualCtc(e.target.value)}
                      placeholder="600000"
                    />
                  </div>
                  <div className="su-salary-stat">
                    <span className="su-salary-stat-label">Monthly gross</span>
                    <span className="su-salary-stat-val">
                      {salaryPreview ? `₹${salaryPreview.monthlyFmt}` : "—"}
                    </span>
                    <span className="su-salary-stat-formula">Annual CTC ÷ 12</span>
                  </div>
                  <div className="su-salary-stat">
                    <span className="su-salary-stat-label">Daily rate</span>
                    <span className="su-salary-stat-val">
                      {salaryPreview ? `₹${salaryPreview.dailyFmt}` : "—"}
                    </span>
                    <span className="su-salary-stat-formula">
                      Monthly ÷ {monthlySalaryDivisorDays || 26} working days
                    </span>
                  </div>
                </div>
                {salaryPreview && (
                  <p className="su-salary-preview-note">
                    Example: ₹{salaryPreview.ctcFmt} per year → ₹{salaryPreview.monthlyFmt}/month →
                    ₹{salaryPreview.dailyFmt}/day for payroll &amp; loss-of-pay.
                  </p>
                )}
              </div>
            )}
          </SectionCard>
          )}

          {/* 10. EMPLOYEE NUMBERING */}
          {stepId === "numbering" && (
          <SectionCard icon={Hash} iconColor="orange" title="Employee Numbering" desc="Choose auto increment or manual codes for this organisation">
            <div className="su-emp-preview-box">
              <span className="su-emp-preview-label">Next employee ID</span>
              <span className={`su-emp-preview-code${liveCodeLoading ? " loading" : ""}`}>
                {liveCodeLoading ? "…" : empPreview}
              </span>
            </div>
            <div className="su-grid-2">
              {empUsePrefix && (
              <div className="su-field" data-field-key="empPrefix">
                <label className="su-label">Prefix</label>
                <input
                  className={`su-input${errors.empPrefix ? " err" : ""}`}
                  value={empPrefix}
                  onChange={e => handleEmpPrefixChange(e.target.value)}
                  placeholder="EMP"
                  style={{ textTransform: "uppercase", fontFamily: "monospace", fontWeight: 700 }}
                  maxLength={4}
                />
                <span className="su-hint">1–4 letters (e.g. EMP, HR, AAA). Turn prefix off below for numbers only.</span>
                <Err msg={errors.empPrefix} />
                {empPrefix.length > 0 && !resolvedPrefix && (
                  <button
                    type="button"
                    className="su-prefix-fix-btn"
                    onClick={() => setEmpPrefix("EMP")}
                  >
                    Use recommended prefix: EMP
                  </button>
                )}
              </div>
              )}
              <div className="su-field">
                <label className="su-label">Starting Number</label>
                <input
                  className="su-input"
                  type="number"
                  min={1}
                  max={999999999}
                  value={empStartNumber}
                  onChange={e => setEmpStartNumber(e.target.value)}
                />
                <span className="su-hint">First number for this organisation (e.g. 1, 1001, 2001, 10001).</span>
              </div>
              <div className="su-field su-span-2">
                <ToggleRow
                  label="Prefix"
                  sub={empUsePrefix
                    ? "On — codes include letters, e.g. ABC0001"
                    : "Off — numbers only, e.g. 0001 or 10001"}
                  checked={empUsePrefix}
                  onChange={setEmpUsePrefix}
                  id="emp-use-prefix"
                />
              </div>
              <div className="su-field su-span-2">
                <ToggleRow
                  label="Auto Increment Employee IDs"
                  sub={empAutoIncrement
                    ? `Live preview: ${liveCodeLoading ? "checking…" : empPreview}`
                    : `Manual entry: HR types each code (preview ${empPreview})`}
                  checked={empAutoIncrement}
                  onChange={(v) => {
                    setEmpAutoIncrement(v);
                    if (v && empUsePrefix && empPrefix.length > 0 && !resolvedPrefix) {
                      setEmpPrefix("EMP");
                    }
                  }}
                  id="emp-auto"
                />
              </div>
            </div>
          </SectionCard>
          )}

          {/* 11. NOTIFICATION SETTINGS */}
          {stepId === "notifications" && (
          <SectionCard icon={Bell} iconColor="amber" title="Notification Settings" desc="Email and in-app alert preferences — save everything from this final step">
            <ToggleRow label="Email Notifications"     sub="Send emails for all key events"            checked={emailNotif}      onChange={setEmailNotif}      id="notif-email" />
            <ToggleRow label="Attendance Alerts"       sub="Late arrivals, absences, exceptions"        checked={attendanceAlert}  onChange={setAttendanceAlert}  id="notif-att" />
            <ToggleRow label="Leave Alerts"            sub="Leave requests and approvals"               checked={leaveAlert}       onChange={setLeaveAlert}       id="notif-leave" />
            <ToggleRow label="Payroll Alerts"          sub="Payroll processing and disbursement"        checked={payrollAlert}     onChange={setPayrollAlert}     id="notif-payroll" />
            <ToggleRow label="Approval Notifications" sub="Pending approvals and status updates"       checked={approvalNotif}    onChange={setApprovalNotif}    id="notif-approval" />
          </SectionCard>
          )}

        </div>

        {/* ── Right: live summary panel ───────────────────────────── */}
        <div className="su-panel">
          <SummaryPanel
            form={{
              orgName,
              legalName,
              tradeName,
              industryType,
              shiftName,
              salaryCycle,
              empPrefix,
              empStart: empStartNumber,
              liveNextCode: empPreview,
              leaveStatus: leaveStatusLabel,
              attendanceReady,
              leaveReady,
              payrollReady,
              logoVersion,
            }}
            departments={departments}
            designations={designations}
            locations={locations}
            logoUrl={logoUrl}
          />
        </div>
      </div>

      {/* Sticky footer */}
      <div className="su-footer">
        <div className="su-footer-info">
          Step <strong>{stepIndex + 1}/{SETUP_STEPS.length}</strong>
          {" · "}
          <strong>{departments.length}</strong> dept · <strong>{designations.length}</strong> desig · <strong>{locations.length}</strong> loc ·{" "}
          Leave: <strong>{leaveStatusLabel}</strong>
        </div>
        <div className="su-footer-actions">
          {!isFirstStep && (
            <button type="button" className="su-btn-outline" onClick={handleBack} disabled={submitting}>
              <ArrowLeft size={15} strokeWidth={2.25} aria-hidden />
              Back
            </button>
          )}
          <button type="button" className="su-btn-outline" onClick={() => navigate("/dashboard")}>
            Cancel
          </button>
          {!isLastStep ? (
            <button type="button" className="su-btn-primary" onClick={handleNext} disabled={submitting}>
              Next
              <ArrowRight size={15} strokeWidth={2.25} aria-hidden />
            </button>
          ) : (
            <button
              type="button"
              className="su-btn-primary"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting
                ? <><span className="su-spinner" /> Saving…</>
                : foundationComplete
                  ? "Save Changes"
                  : "Complete Setup →"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
